import { useState, useEffect, useCallback, useMemo } from 'react'
import { upsertIndicador, getIndicadorComparativo } from '@/services/api'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts'
import { TrendingUp, DollarSign, Activity, Save, BarChart3, LayoutDashboard, Edit3, Filter } from 'lucide-react'
import { toast } from 'sonner'
import { formatCurrency } from '@/lib/utils'

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

const TIPOS = {
  custo: [
    { tipo: 'energia', label: 'Energia Elétrica', isCurrency: true, color: '#F59E0B' },
    { tipo: 'agua', label: 'Água', isCurrency: true, color: '#3B82F6' },
    { tipo: 'produto_limpeza', label: 'Produtos de Limpeza', isCurrency: true, color: '#10B981' },
    { tipo: 'alimentacao', label: 'Alimentação', isCurrency: true, color: '#EF4444' },
    { tipo: 'farmacia', label: 'Farmácia', isCurrency: true, color: '#8B5CF6' },
  ],
  atendimento: [
    { tipo: 'num_atendimentos', label: 'Nº de Atendimentos', isCurrency: false, color: '#0EA5E9' },
    { tipo: 'tempo_espera', label: 'Tempo Médio Espera (min)', isCurrency: false, color: '#F59E0B' },
    { tipo: 'num_internacoes', label: 'Nº de Internações', isCurrency: false, color: '#EF4444' },
    { tipo: 'tempo_internacao', label: 'Tempo Médio Internação (dias)', isCurrency: false, color: '#8B5CF6' },
  ],
}

function formatValue(v, isCurrency) {
  if (!v) return '-'
  return isCurrency ? formatCurrency(v) : v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })
}

function buildValuesByTipo(comparativo, categoria) {
  const map = {}
  comparativo?.mensal?.filter(m => m.categoria === categoria).forEach(m => { map[m.tipo] = m.valores })
  return map
}

// =================== LANÇAMENTO (tabela editável) ===================
function LancamentoTabela({ ano, categoria, comparativo, onSave }) {
  const tipos = TIPOS[categoria]
  const [editValues, setEditValues] = useState({})
  const valuesByTipo = useMemo(() => buildValuesByTipo(comparativo, categoria), [comparativo, categoria])

  const handleChange = (tipo, mes, value) => {
    setEditValues(prev => ({ ...prev, [`${tipo}-${mes}`]: value }))
  }

  const handleBlur = async (tipo, mes) => {
    const key = `${tipo}-${mes}`
    const raw = editValues[key]
    if (raw === undefined || raw === '') return
    const valor = parseFloat(raw)
    if (isNaN(valor) || valor < 0) {
      toast.error('Valor inválido')
      return
    }
    const currentValor = valuesByTipo[tipo]?.[mes - 1] || 0
    if (valor === currentValor) return
    try {
      await onSave({ ano, mes, categoria, tipo, valor })
      toast.success(`${MESES[mes - 1]}/${ano} salvo`)
      setEditValues(prev => { const n = { ...prev }; delete n[key]; return n })
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar')
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {categoria === 'custo' ? <DollarSign className="w-5 h-5 text-amber-600" /> : <Activity className="w-5 h-5 text-blue-600" />}
          Lançamento Mensal — {ano}
        </CardTitle>
        <CardDescription>
          Clique em qualquer célula para editar. Salvamento automático ao sair (Tab/Enter ou clique fora).
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 sticky top-0">
              <TableHead className="min-w-[220px]">Indicador</TableHead>
              {MESES.map(m => <TableHead key={m} className="text-center min-w-[100px]">{m}</TableHead>)}
              <TableHead className="text-right bg-slate-100">Total</TableHead>
              <TableHead className="text-right bg-slate-100">Média</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tipos.map(t => {
              const vals = valuesByTipo[t.tipo] || []
              const total = vals.reduce((s, v) => s + v, 0)
              const meses = vals.filter(v => v > 0).length
              const media = meses > 0 ? total / meses : 0
              return (
                <TableRow key={t.tipo}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />
                      {t.label}
                    </div>
                  </TableCell>
                  {MESES.map((_, idx) => {
                    const mes = idx + 1
                    const key = `${t.tipo}-${mes}`
                    const stored = vals[idx] || 0
                    const editing = editValues[key]
                    return (
                      <TableCell key={mes} className="p-1">
                        <Input
                          type="number"
                          step={t.isCurrency ? '0.01' : '1'}
                          min="0"
                          value={editing !== undefined ? editing : (stored || '')}
                          onChange={(e) => handleChange(t.tipo, mes, e.target.value)}
                          onBlur={() => handleBlur(t.tipo, mes)}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur() }}
                          className="h-8 text-right text-sm"
                          placeholder="-"
                          data-testid={`input-${categoria}-${t.tipo}-${mes}`}
                        />
                      </TableCell>
                    )
                  })}
                  <TableCell className="text-right font-semibold bg-slate-50">
                    {formatValue(total, t.isCurrency)}
                  </TableCell>
                  <TableCell className="text-right text-slate-600 bg-slate-50">
                    {formatValue(media, t.isCurrency)}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

// =================== DASHBOARD (somente visualizações) ===================
function DashboardCategoria({ ano, categoria, comparativo }) {
  const tipos = TIPOS[categoria]
  const valuesByTipo = useMemo(() => buildValuesByTipo(comparativo, categoria), [comparativo, categoria])
  const [mesInicio, setMesInicio] = useState(1)
  const [mesFim, setMesFim] = useState(12)

  const startIdx = Math.min(mesInicio, mesFim) - 1
  const endIdx = Math.max(mesInicio, mesFim) - 1

  const chartData = MESES
    .map((m, idx) => {
      const row = { mes: m, _idx: idx }
      tipos.forEach(t => { row[t.tipo] = valuesByTipo[t.tipo]?.[idx] || 0 })
      return row
    })
    .filter(r => r._idx >= startIdx && r._idx <= endIdx)

  const trimestralData = useMemo(() => {
    const trimestres = ['T1 (Jan-Mar)', 'T2 (Abr-Jun)', 'T3 (Jul-Set)', 'T4 (Out-Dez)']
    return trimestres.map((tri, ti) => {
      const triStart = ti * 3
      const triEnd = ti * 3 + 2
      // Mantém apenas trimestres que possuem ao menos 1 mês dentro do range selecionado
      if (triEnd < startIdx || triStart > endIdx) return null
      const row = { trimestre: tri }
      tipos.forEach(t => {
        const vals = valuesByTipo[t.tipo] || []
        // soma apenas os meses do trimestre que estão dentro do range
        let soma = 0
        for (let i = triStart; i <= triEnd; i++) {
          if (i >= startIdx && i <= endIdx) soma += vals[i] || 0
        }
        row[t.tipo] = soma
      })
      return row
    }).filter(Boolean)
  }, [valuesByTipo, tipos, startIdx, endIdx])

  const totaisPorTipo = useMemo(() => tipos.map(t => {
    const vals = valuesByTipo[t.tipo] || []
    const filtered = vals.slice(startIdx, endIdx + 1)
    const total = filtered.reduce((s, v) => s + v, 0)
    const meses = filtered.filter(v => v > 0).length
    return { ...t, total, media: meses > 0 ? total / meses : 0, meses }
  }), [tipos, valuesByTipo, startIdx, endIdx])

  const totalCategoria = totaisPorTipo.reduce((s, t) => s + t.total, 0)
  const isCurrencyCategoria = categoria === 'custo'
  const periodoLabel = startIdx === endIdx
    ? `${MESES[startIdx]}/${ano}`
    : `${MESES[startIdx]} a ${MESES[endIdx]}/${ano}`

  return (
    <div className="space-y-6">
      {/* Filtro de período */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-col md:flex-row md:items-end gap-3 md:gap-4">
            <div className="flex items-center gap-2 text-slate-700 font-medium text-sm">
              <Filter className="w-4 h-4 text-slate-500" />
              Filtrar período:
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-1">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">De</Label>
                <Select value={mesInicio.toString()} onValueChange={(v) => setMesInicio(parseInt(v))}>
                  <SelectTrigger className="w-32" data-testid="filtro-mes-inicio">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MESES.map((m, i) => <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Até</Label>
                <Select value={mesFim.toString()} onValueChange={(v) => setMesFim(parseInt(v))}>
                  <SelectTrigger className="w-32" data-testid="filtro-mes-fim">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MESES.map((m, i) => <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap gap-2 items-end">
                <Button variant="outline" size="sm" onClick={() => { setMesInicio(1); setMesFim(3) }} data-testid="preset-t1">T1</Button>
                <Button variant="outline" size="sm" onClick={() => { setMesInicio(4); setMesFim(6) }} data-testid="preset-t2">T2</Button>
                <Button variant="outline" size="sm" onClick={() => { setMesInicio(7); setMesFim(9) }} data-testid="preset-t3">T3</Button>
                <Button variant="outline" size="sm" onClick={() => { setMesInicio(10); setMesFim(12) }} data-testid="preset-t4">T4</Button>
                <Button variant="outline" size="sm" onClick={() => { setMesInicio(1); setMesFim(12) }} data-testid="preset-ano">Ano</Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cards de resumo */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
        {totaisPorTipo.map(t => (
          <Card key={t.tipo} className="border-l-4" style={{ borderLeftColor: t.color }}>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-slate-500">{t.label}</p>
              <p className="text-lg md:text-2xl font-semibold text-slate-900 mt-1 break-words">{formatValue(t.total, t.isCurrency)}</p>
              <p className="text-xs text-slate-500 mt-1">Média: {formatValue(t.media, t.isCurrency)} ({t.meses} mês{t.meses === 1 ? '' : 'es'})</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Total geral */}
      <Card className="bg-gradient-to-r from-teal-50 to-blue-50 border-teal-200">
        <CardContent className="pt-6 pb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <p className="text-sm text-teal-700 font-medium">Total {categoria === 'custo' ? 'de Custos' : 'do Período'} — {periodoLabel}</p>
            <p className="text-3xl font-bold text-teal-900 mt-1">
              {formatValue(totalCategoria, isCurrencyCategoria)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">{tipos.length} indicadores monitorados</p>
          </div>
        </CardContent>
      </Card>

      {/* Gráfico mensal */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-teal-600" />
            Comparativo Mensal — {periodoLabel}
          </CardTitle>
          <CardDescription>Evolução mês a mês no período selecionado</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72 md:h-96">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis />
                <RechartsTooltip
                  formatter={(value, name) => {
                    const t = tipos.find(x => x.tipo === name)
                    return [formatValue(value, t?.isCurrency), t?.label || name]
                  }}
                />
                <Legend formatter={(value) => tipos.find(t => t.tipo === value)?.label || value} />
                {tipos.map(t => (
                  <Line key={t.tipo} type="monotone" dataKey={t.tipo} stroke={t.color} strokeWidth={2} dot={{ r: 4 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Gráfico trimestral */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-purple-600" />
            Comparativo Trimestral — {periodoLabel}
          </CardTitle>
          <CardDescription>Soma de cada trimestre dentro do período selecionado</CardDescription>
        </CardHeader>
        <CardContent>
          {trimestralData.length === 0 ? (
            <div className="h-32 flex items-center justify-center text-slate-400 text-sm">
              Nenhum trimestre dentro do período selecionado
            </div>
          ) : (
            <div className="h-72 md:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trimestralData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="trimestre" />
                  <YAxis />
                  <RechartsTooltip
                    formatter={(value, name) => {
                      const t = tipos.find(x => x.tipo === name)
                      return [formatValue(value, t?.isCurrency), t?.label || name]
                    }}
                  />
                  <Legend formatter={(value) => tipos.find(t => t.tipo === value)?.label || value} />
                  {tipos.map(t => (
                    <Bar key={t.tipo} dataKey={t.tipo} fill={t.color} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// =================== PÁGINA PRINCIPAL ===================
export default function IndicadoresPage() {
  const currentYear = new Date().getFullYear()
  const [ano, setAno] = useState(currentYear)
  const [comparativo, setComparativo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState('lancamento')  // 'lancamento' | 'dashboard'
  const [categoriaLancamento, setCategoriaLancamento] = useState('custo')
  const [categoriaDashboard, setCategoriaDashboard] = useState('custo')

  const fetchComparativo = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    try {
      const response = await getIndicadorComparativo(ano)
      setComparativo(response.data)
    } catch (error) {
      toast.error('Erro ao carregar indicadores')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [ano])

  useEffect(() => { fetchComparativo() }, [fetchComparativo])

  const handleSave = async (data) => {
    await upsertIndicador(data)
    fetchComparativo({ silent: true })
  }

  const anos = []
  for (let y = currentYear + 1; y >= currentYear - 5; y--) anos.push(y)

  return (
    <div className="space-y-6 md:space-y-8 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight flex items-center gap-3">
            <TrendingUp className="w-7 h-7 md:w-8 md:h-8 text-teal-600" />
            Indicadores
          </h1>
          <p className="text-sm md:text-base text-slate-500 mt-1">Acompanhamento mensal e trimestral de custos e atendimentos</p>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="ano-select" className="text-sm">Ano:</Label>
          <Select value={ano.toString()} onValueChange={(v) => setAno(parseInt(v))}>
            <SelectTrigger className="w-32" data-testid="select-ano-indicadores">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {anos.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <Card>
          <CardContent className="flex items-center justify-center h-64 text-slate-500">
            <Save className="w-8 h-8 animate-pulse" />
          </CardContent>
        </Card>
      ) : (
        <Tabs value={view} onValueChange={setView} className="space-y-4">
          <TabsList className="grid grid-cols-2 w-full max-w-md">
            <TabsTrigger value="lancamento" data-testid="tab-lancamento">
              <Edit3 className="w-4 h-4 mr-2" />
              Lançamento
            </TabsTrigger>
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Dashboard
            </TabsTrigger>
          </TabsList>

          <TabsContent value="lancamento">
            <Tabs value={categoriaLancamento} onValueChange={setCategoriaLancamento} className="space-y-4">
              <TabsList>
                <TabsTrigger value="custo" data-testid="sub-tab-custos-lanc">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Custos
                </TabsTrigger>
                <TabsTrigger value="atendimento" data-testid="sub-tab-atendimentos-lanc">
                  <Activity className="w-4 h-4 mr-2" />
                  Atendimentos
                </TabsTrigger>
              </TabsList>
              <TabsContent value="custo">
                <LancamentoTabela ano={ano} categoria="custo" comparativo={comparativo} onSave={handleSave} />
              </TabsContent>
              <TabsContent value="atendimento">
                <LancamentoTabela ano={ano} categoria="atendimento" comparativo={comparativo} onSave={handleSave} />
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="dashboard">
            <Tabs value={categoriaDashboard} onValueChange={setCategoriaDashboard} className="space-y-4">
              <TabsList>
                <TabsTrigger value="custo" data-testid="sub-tab-custos-dash">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Custos
                </TabsTrigger>
                <TabsTrigger value="atendimento" data-testid="sub-tab-atendimentos-dash">
                  <Activity className="w-4 h-4 mr-2" />
                  Atendimentos
                </TabsTrigger>
              </TabsList>
              <TabsContent value="custo">
                <DashboardCategoria ano={ano} categoria="custo" comparativo={comparativo} />
              </TabsContent>
              <TabsContent value="atendimento">
                <DashboardCategoria ano={ano} categoria="atendimento" comparativo={comparativo} />
              </TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>
      )}
    </div>
  )
}
